/**
 * The app root: boot, the dev-gallery escape hatch, and the ONE breakpoint that
 * chooses between the two view trees (INVENTORY-DESKTOP §10.3).
 *
 *   const wide = useBreakpoint();                 // matchMedia (min-width:760px)
 *   return wide ? <DesktopApp/> : <MobileApp/>;
 *
 * TWO TREES, NOT A RESPONSIVE ONE. `MobileApp` is the 430px column shell that
 * used to BE this file (moved verbatim in lane D0); `desktop/DesktopApp` is the
 * full-bleed shell with the 58px top nav. They share the store, the selectors,
 * `lib/`, `repo/` and `components/` — and nothing else. The desktop tree never
 * imports from `screens/` or `sheets/`, and the mobile tree never imports from
 * `desktop/` (INVENTORY-DESKTOP §10). Enforce that by review.
 *
 * WHY 760px: it is the prototype's own switch verbatim (`isMobile = S.w < 760`)
 * and the GOTY two-column floor — `flex:2 min-width:340` + `gap:22` + a 320px
 * rail + 2×26px gutters is 760 exactly. Below it the desktop layout collapses,
 * so we hand the whole viewport to the mobile build rather than reproducing the
 * prototype's hybrid chrome (desktop bodies under a mobile bar + 7-tab bottom
 * bar, §2.3) — a deliberate, recorded deviation.
 *
 * SWITCHING TREES UNMOUNTS ONE AND MOUNTS THE OTHER. That is intentional: it
 * keeps each tree's overlay hosts, z-ladder and CSS scope self-contained. The
 * store survives (it is a module singleton), so nothing about the session,
 * the boards or an in-flight write is affected. `DesktopApp` tears down its two
 * imperative subsystems (the drag ghost, the hover-tilt rAF) on the way out.
 *
 * The one thing an unmount CANNOT survive is a fullscreen media element, and a
 * phone crosses 760px just by being rotated (~390px portrait, ~850px landscape)
 * — which closed any trailer the user had gone fullscreen on to enlarge. So
 * `useBreakpoint` defers the crossing until fullscreen ends; the reasoning and
 * its one known gap live in that file, and nothing here has to know.
 *
 * Boot lives HERE, above the switch, so crossing the breakpoint never re-boots.
 */

import { useEffect } from 'react';

import type { Accent } from './components/theme.ts';
import { useBreakpoint } from './desktop/useBreakpoint.ts';
import DesktopApp from './desktop/DesktopApp.tsx';
import { DevGallery, isGalleryRoute } from './dev/Gallery.tsx';
import MobileApp from './MobileApp.tsx';
import { useQd } from './store/index.ts';

/**
 * INVENTORY §10: the prototype's `accent` was a design-time prop, and the ui
 * slice has no field for it yet. Swap this for `useQd((s) => s.accent)` the day
 * one lands — `accentVars` already implements the magenta-flips-acc2 rule.
 * Both trees take it as a prop so there is exactly one source of truth.
 */
const ACCENT: Accent = 'green';
/** INVENTORY §2 row 11 — design-time only, same story as ACCENT. */
const SCANLINES: boolean = false;

export function App() {
  const boot = useQd((s) => s.boot);
  const teardown = useQd((s) => s.teardown);
  const wide = useBreakpoint();

  useEffect(() => {
    void boot();
    return () => teardown();
  }, [boot, teardown]);

  if (import.meta.env.DEV && isGalleryRoute()) return <DevGallery />;

  return wide ? (
    <DesktopApp accent={ACCENT} scanlines={SCANLINES} />
  ) : (
    <MobileApp accent={ACCENT} scanlines={SCANLINES} />
  );
}
